"use client";

import React, { useState } from "react";
import { CookiePreferences, useCookieConsent } from "@/contexts/CookieConsentContext";
import { Check, X } from "lucide-react";

export function CookiePreferencesModal({ onClose }: { onClose: () => void }) {
    const { preferences, savePreferences } = useCookieConsent();
    const [localPrefs, setLocalPrefs] = useState<CookiePreferences>(preferences);

    const handleSave = () => {
        savePreferences(localPrefs);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-surface-2 dark:bg-zinc-900 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
                <div className="p-6 border-b border-gray-100 dark:border-zinc-800 flex justify-between items-start">
                    <div>
                        <h2 className="text-xl font-bold font-inter text-gray-900 dark:text-white">Configuración de Privacidad</h2>
                        <p className="text-sm text-gray-500 dark:text-zinc-400 mt-1">
                            Personaliza el uso de cookies. Los cambios aplicarán inmediatamente tras guardar.
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 -mr-2 text-gray-400 hover:text-gray-600 dark:hover:text-zinc-300 rounded-full transition-colors"
                        aria-label="Cerrar modal"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
                    {/* Estrictamente Necesarias */}
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                Estrictamente necesarias
                                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-tint-accent-strong text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                                    Siempre activas
                                </span>
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-zinc-400 mt-1">
                                Son esenciales para que la página funcione (ej. tu sesión, tus preferencias visuales no rastreables, seguridad, y guardar tu configuración de privacidad).
                            </p>
                        </div>
                        {/* Pseudo-toggle (siempre activo y deshabilitado) */}
                        <div className="shrink-0 pt-1">
                            <div className="w-11 h-6 bg-blue-600 rounded-full relative opacity-50 cursor-not-allowed">
                                <div className="absolute top-1 left-6 w-4 h-4 rounded-full bg-surface-2 shadow flex items-center justify-center">
                                    <Check size={10} className="text-blue-600" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="h-px bg-tint-neutral-strong dark:bg-zinc-800" />

                    {/* Analíticas */}
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 cursor-pointer" onClick={() => setLocalPrefs(prev => ({ ...prev, analytics: !prev.analytics }))}>
                            <h3 id="cookie-pref-analytics" className="text-base font-semibold text-gray-900 dark:text-white">
                                Analíticas (Rendimiento)
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-zinc-400 mt-1 pr-4">
                                Nos permiten medir las visitas a la web pública con Google Analytics (cookies _ga) y su rendimiento con Vercel Analytics y Speed Insights. Solo se activan si las aceptas, nunca dentro de la aplicación, y si las desactivas dejamos de medir y borramos las cookies _ga.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setLocalPrefs(prev => ({ ...prev, analytics: !prev.analytics }))}
                            className="shrink-0 pt-1 relative outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-600 rounded-full"
                            role="switch"
                            aria-checked={localPrefs.analytics}
                            aria-labelledby="cookie-pref-analytics"
                        >
                            <div className={`w-11 h-6 rounded-full transition-colors ${localPrefs.analytics ? 'bg-blue-600' : 'bg-gray-300 dark:bg-zinc-700'}`}>
                                <div className={`absolute top-1 w-4 h-4 rounded-full bg-surface-2 shadow flex items-center justify-center transition-transform ${localPrefs.analytics ? 'translate-x-6' : 'translate-x-1'}`}>
                                    {localPrefs.analytics && <Check size={10} className="text-blue-600" />}
                                </div>
                            </div>
                        </button>
                    </div>

                    <div className="h-px bg-tint-neutral-strong dark:bg-zinc-800" />

                    {/* Marketing (Para futuro o si se añaden píxeles) */}
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 cursor-pointer" onClick={() => setLocalPrefs(prev => ({ ...prev, marketing: !prev.marketing }))}>
                            <h3 id="cookie-pref-marketing" className="text-base font-semibold text-gray-900 dark:text-white">
                                Publicidad y Marketing
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-zinc-400 mt-1 pr-4">
                                Se utilizan para rastrear a los visitantes en las páginas web y mostrar anuncios relevantes. Actualmente no usamos cookies de terceros de esta categoría, pero te damos control del consentimiento de antemano.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setLocalPrefs(prev => ({ ...prev, marketing: !prev.marketing }))}
                            className="shrink-0 pt-1 relative outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-600 rounded-full"
                            role="switch"
                            aria-checked={localPrefs.marketing}
                            aria-labelledby="cookie-pref-marketing"
                        >
                            <div className={`w-11 h-6 rounded-full transition-colors ${localPrefs.marketing ? 'bg-blue-600' : 'bg-gray-300 dark:bg-zinc-700'}`}>
                                <div className={`absolute top-1 w-4 h-4 rounded-full bg-surface-2 shadow flex items-center justify-center transition-transform ${localPrefs.marketing ? 'translate-x-6' : 'translate-x-1'}`}>
                                    {localPrefs.marketing && <Check size={10} className="text-blue-600" />}
                                </div>
                            </div>
                        </button>
                    </div>
                </div>

                <div className="p-6 bg-tint-neutral dark:bg-zinc-900/50 border-t border-gray-100 dark:border-zinc-800 flex justify-end gap-3 flex-col sm:flex-row">
                    <button
                        onClick={onClose}
                        className="px-5 py-2.5 rounded-xl font-medium text-gray-700 dark:text-zinc-300 bg-surface-2 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 hover:bg-tint-neutral dark:hover:bg-zinc-750 transition-colors w-full sm:w-auto"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-5 py-2.5 rounded-xl font-medium text-white bg-blue-600 hover:bg-blue-700 shadow-sm shadow-blue-600/20 transition-all w-full sm:w-auto"
                    >
                        Guardar preferencias
                    </button>
                </div>
            </div >
        </div >
    );
}
